import * as React from 'react'
import { useQuery } from '@apollo/client'

import { graphql } from '~publish/gql'
import type { AppDispatch } from '~publish/legacy/store'

import type { AccountContextValue } from './types'

export const AccountContext = React.createContext<AccountContextValue | null>(
  null,
)

export const AccountContextProvider: React.FC<{
  children: React.ReactNode
  // dispatch is needed to avoid importing all of the store
  // which makes some tests fail because of import cycles
  dispatch: AppDispatch
}> = ({ children, dispatch }) => {
  const {
    loading,
    data: account,
    refetch: refetchAccount,
  } = useQuery(GetAccount)

  const { ORGANIZATION_EVENT_KEY } = window?.appshell?.eventKeys || {}
  const { billingUpdated } = window?.appshell?.actionKeys || {}

  React.useEffect(() => {
    function onOrgChange({ detail }: { detail: { action: string } }): void {
      if (!detail.action) {
        void refetchAccount()
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      } else if (detail.action === billingUpdated) {
        void refetchAccount()
        dispatch({ type: 'INIT_ORGANIZATIONS' })
      }
    }
    window.addEventListener(ORGANIZATION_EVENT_KEY, onOrgChange)

    return function cleanup(): void {
      window.removeEventListener(ORGANIZATION_EVENT_KEY, onOrgChange)
    }
  }, [refetchAccount, ORGANIZATION_EVENT_KEY, billingUpdated, dispatch])

  const accountContextValue: AccountContextValue = {
    account: account?.account ?? null,
    loading,
    refetchAccount,
  }

  return (
    <AccountContext.Provider value={accountContextValue}>
      {children}
    </AccountContext.Provider>
  )
}

export const GetAccount = graphql(/* GraphQL */ `
  query GetAccount {
    account {
      id
      name
      email
      backupEmail
      hasVerifiedEmail
      hasVerifiedBackupEmail
      avatar
      featureFlips
      createdAt
      dismissedBanners
      tfa {
        type
      }
      timezone
      preferences {
        timeFormat
        startOfWeek
        defaultScheduleOption
      }
      settings {
        aiSettings {
          onboarded
        }
      }
      currentOrganization {
        id
        isOneBufferOrganization
        isBetaEnabled
        entitlements
        createdAt
        ownerEmail
        members {
          totalCount
        }
        privileges {
          canManageChannels
          canManageBilling
          canManageAllNotes
          canEdit
          canTransferOwnership
        }
        channels {
          id
          name
          service
          organizationId
          isLocked
          accessLevel
          descriptor
          avatar
        }
        commonTrackingProperties {
          atEventBillingState
          atEventBillingPlan
          atEventBillingCycle
          atEventBillingGateway
          atEventIsNewBuffer
          atEventChannelQty
        }
        dismissedObjects {
          id
        }
        name
        role
        billing {
          __typename
          canAccessPublishing
          ... on OBBilling {
            id
            canStartTrial
            subscription {
              interval
              plan {
                id
              }
              trial {
                isActive
              }
            }
            changePlanOptions {
              planId
              planInterval
              isRecommended
            }
            gateway {
              gatewayType
            }
            paymentDetails {
              hasPaymentDetails
              paymentMethod
            }
            pricingTiers {
              flatFee
              unitPrice
              upperBound
            }
          }
        }
        limits {
          ideas
          channels
          scheduledPosts
          tags
          savedReplies
        }
      }
    }
  }
`)
